import { useUpcomingCallsQuery } from './index'
import { secondsToMs } from '@thyme/libs/src/time/convert'
import { DateTime } from 'luxon'
import { onMounted, onUnmounted, ref, watch } from 'vue'
import type { UpcomingCall } from './types'

// How often we re-check stored calls' start times against the current time.
const TICK_INTERVAL_SECONDS = 30

// How far ahead of a call's start we announce it.
export const REMINDER_LEAD_MINUTES = 5

/**
 * Keyed on id & start time, so a rescheduled call is able to re-announce
 * (as a toast id)
 * @param call
 */
export function buildAnnouncementKey(call: UpcomingCall): string {
  if (call.isDateOnly) return `${call.communicationId}:day:${call.dueDate}`
  return `${call.communicationId}:at:${call.startTime.toISO()}`
}

/**
 * @param onCallsDue called with the calls that just came due, never with an empty array
 */
export function useUpcomingCallReminders(
  onCallsDue: (calls: UpcomingCall[]) => void
): void {
  const day = ref(DateTime.now())
  const { data: calls } = useUpcomingCallsQuery(day)
  // announcementKeys bounded by one session's calls
  const announced = new Set<string>()
  let tickHandle: ReturnType<typeof setInterval> | undefined

  /**
   * Announces any calls now within the lead time
   */
  function checkForDueCalls() {
    const now = DateTime.now()
    // Only reassign on a date change, so the query key stays stable within a day
    if (!now.hasSame(day.value, 'day')) day.value = now

    const callsDue = (calls.value ?? []).filter((call) => {
      // IMPROVEME(MET-3034): date-only calls announce on first action of the day, not off a start time
      if (call.isDateOnly) return false
      if (announced.has(buildAnnouncementKey(call))) return false
      const minutesUntilStart = call.startTime.diff(now).as('minutes')
      // Forward-looking only: a call whose start has passed is ignored (may need adjustment for all day calls in future)
      return (
        minutesUntilStart >= 0 && minutesUntilStart <= REMINDER_LEAD_MINUTES
      )
    })

    if (!callsDue.length) return

    for (const call of callsDue) announced.add(buildAnnouncementKey(call))
    onCallsDue(callsDue)
  }

  // Added to address checks as soon as data lands because data sometimes lands post
  // mount, if flags hadn't loaded yet. Also fires every time the day's calls that the
  // query returns changes (select result is memoized, so uses cache if no change
  // to calls on query rather than rerunning toUpcomingCall())
  watch(calls, () => {
    checkForDueCalls()
  })

  onMounted(() => {
    // Check on mount too, so a call already inside the lead time isn't held up
    checkForDueCalls()
    tickHandle = setInterval(
      checkForDueCalls,
      secondsToMs(TICK_INTERVAL_SECONDS)
    )
  })

  onUnmounted(() => {
    clearInterval(tickHandle)
  })
}
